import React from 'react';

import * as Yup from 'yup';
import { useMutation } from '@tanstack/react-query';

import Modal from '../../../../../Modal';
import CrudForm from '../../../../../Form/CrudForm';

import { useUiStore } from '../../../../../../store';
import CreditReportService from '../../../../../../services/CreditReportService';
import CreditReportTradelineService from '../../../../../../services/CreditReportTradelineService';

const fields = [
  {
    label: 'Creditor',
    name: 'creditor',
    type: 'text',
    placeholder: 'Enter creditor name',
    validation: Yup.string().required('This field is required'),
    fullWidth: true,
  },
  {
    label: 'Type',
    name: 'type',
    type: 'select',
    placeholder: 'Select a type',
    options: [
      { label: 'AUTO', value: 'AUTO' },
      { label: 'EDU', value: 'EDU' },
      { label: 'INST', value: 'INST' },
      { label: 'REV', value: 'REV' },
      { label: 'MTG', value: 'MTG' },
      { label: 'COLL', value: 'COLL' },
      { label: 'OPEN', value: 'OPEN' },
      { label: 'SEC', value: 'SEC' },
      { label: 'LEASE', value: 'LEASE' },
    ],
    validation: Yup.string().required('This field is required'),
  },
  {
    label: 'Balance',
    name: 'balance',
    type: 'currency',
    placeholder: 'Enter balance',
    validation: Yup.string(),
  },
  {
    label: 'Payment',
    name: 'payment',
    type: 'currency',
    placeholder: 'Enter payment amount',
    validation: Yup.string(),
  },
  {
    label: 'Past due',
    name: 'pastDue',
    type: 'currency',
    placeholder: 'Enter past due amount',
    validation: Yup.string(),
  },
  {
    label: 'Status',
    name: 'status',
    type: 'select',
    placeholder: 'Select a status',
    options: [
      { label: 'AS AGREED', value: 'AS AGREED' },
      { label: 'PAID', value: 'PAID' },
      { label: 'COLLECTION', value: 'COLLECTION' },
      { label: 'CHARGE OFF', value: 'CHARGE OFF' },
      { label: 'CUR WAS 30', value: 'CUR WAS 30' },
      { label: 'CUR WAS 60', value: 'CUR WAS 60' },
      { label: 'CUR WAS 90', value: 'CUR WAS 90' },
      { label: 'FORECLOSURE', value: 'FORECLOSURE' },
      { label: 'LATE 30', value: 'LATE 30' },
      { label: 'LATE 60', value: 'LATE 60' },
      { label: 'LATE 90', value: 'LATE 90' },
      { label: 'LATE 120+', value: 'LATE 120+' },
    ],
    validation: Yup.string(),
  },
  {
    label: 'Real Estate',
    name: 'isRealEstate',
    type: 'checkbox',
    validation: Yup.bool(),
  },
  {
    label: 'Pay Off',
    name: 'isPayOff',
    type: 'checkbox',
    validation: Yup.bool(),
  },
  {
    label: 'Pay Off Verified',
    name: 'isPayOffVerified',
    type: 'checkbox',
    validation: Yup.bool(),
  },
];

export default function AddOrEditTradelineModal({
  isEdit,
  isVisible,
  hideModal,
  creditReportId,
  refetchBorrowers,
  data,
}) {
  const notifySuccess = useUiStore((state) => state.notifySuccess);
  const notifyApiError = useUiStore((state) => state.notifyApiError);

  const { mutate: createCreditReportTradeline, isPending: isCreating } =
    useMutation({
      mutationFn: async (formValues) => {
        return await CreditReportService.createCreditReportTradeline({
          formValues,
          id: creditReportId,
        });
      },
      onSuccess: () => {
        notifySuccess('Tradeline added successfully');
        refetchBorrowers();
        hideModal();
      },
      onError: (error) => {
        notifyApiError(error);
      },
    });

  const { mutate: updateCreditReportService, isPending: isUpdating } =
    useMutation({
      mutationFn: async (formValues) => {
        return await CreditReportTradelineService.updateCreditReportTradeline({
          formValues,
          id: data.id,
        });
      },
      onSuccess: () => {
        notifySuccess('Tradeline updated successfully');
        refetchBorrowers();
        hideModal();
      },
      onError: (error) => {
        notifyApiError(error);
      },
    });

  const isPending = isCreating || isUpdating;

  return (
    <Modal
      zIndex={1000}
      isVisible={isVisible}
      hideModal={hideModal}
      title={isEdit ? 'Edit Tradeline' : 'Add Tradeline'}
    >
      <div className="sm:min-w-[600px] lg:min-w-[900px]">
        <CrudForm
          fields={fields}
          createHandler={createCreditReportTradeline}
          updateHandler={updateCreditReportService}
          submitButtonLabel="Submit"
          isPending={isPending}
          data={data}
        />
      </div>
    </Modal>
  );
}
